import isFunction from "lodash/isFunction";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import {
  isCourseSpace,
  isDraftSpace,
  isEventsSpace,
} from "@circle-react/helpers/spaceHelpers";
import {
  isImagesSpace,
  isMembersSpace,
} from "@circle-react/helpers/spaceHelpers";
import { useLgScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { Space } from "@circle-react/types";
import type { RegularOption } from "@circle-react-shared/uikit/Dropdown/Option";
import { useCoverImageLabel } from "./useCoverImageLabel";
import { useCustomizeMembers } from "./useCustomizeMembers";
import { spaceIconsV3 } from "./useSpaceOptionsIcons";

const customizeSection = t("customize_space.customize");
const customizeOptions = t("customize_space.options");
const customizePaywalls = t("customize_space.paywalls.title");
const customizeMembers = t("customize_space.members");
const customizeLessons = t("customize_space.lessons");
const customizeLockScreen = t("customize_space.lock_screen");
const setWelcomeBanner = t("customize_space.set_welcome_banner");
const customizeWorkflows = t("customize_space.workflows");
const addSpaceLinks = t("add_space_links");

export interface UseManageSpaceOptionsProps {
  onAddCoverClick?: () => void;
  onInviteMemberClick?: () => void;
  openAddSpaceLinksModal: () => void;
  openCustomizeModal: (props: {
    initialSection: string;
    scrollToId?: string;
  }) => void;
  space: Space;
}

export const useManageSpaceOptions = ({
  space,
  onAddCoverClick,
  onInviteMemberClick,
  openCustomizeModal,
  openAddSpaceLinksModal,
}: UseManageSpaceOptionsProps) => {
  const { currentCommunitySettings, currentCommunityMember } =
    usePunditUserContext();

  const isSpaceDescriptionEnabled =
    !!currentCommunitySettings?.space_description_enabled;
  const isUpgradeFlowsForWorkflowsSpacesEnabled =
    !!currentCommunitySettings?.upgrade_flows_for_workflows_spaces_enabled;

  const isCourse = isCourseSpace(space);
  const shouldShowAddCoverImage = isFunction(onAddCoverClick);
  const shouldShowInviteMember = isFunction(onInviteMemberClick);
  const shouldShowLessons = isCourse;
  const isEventsSpaceType = isEventsSpace(space);
  const isLgScreen = useLgScreenMediaQuery();
  const coverImageLabel = useCoverImageLabel({ space });
  const { customizeMembersBadge } = useCustomizeMembers({ space });

  const {
    coverImageIcon,
    customizeLessonsIcon,
    welcomeBannerIcon,
    customizeIcon,
    creditCardIcon,
    customizeLockScreenIcon,
    customizeMembersIcon,
    inviteMemberIcon,
    customizeOptionsIcon,
    addSpaceLinksIcon,
  } = spaceIconsV3;

  const manageSpaceOptions: RegularOption[] = [];

  if (shouldShowAddCoverImage) {
    manageSpaceOptions.push({
      value: t("add_cover_image"),
      label: coverImageLabel,
      onClick: onAddCoverClick,
      icon: coverImageIcon,
      iconSize: 20,
    });
  }

  if (!isImagesSpace(space) && !isMembersSpace(space)) {
    manageSpaceOptions.push({
      value: addSpaceLinks,
      label: addSpaceLinks,
      onClick: () => openAddSpaceLinksModal(),
      icon: addSpaceLinksIcon,
      iconSize: 20,
    });
  }

  if (shouldShowLessons) {
    manageSpaceOptions.push({
      value: customizeLessons,
      label: customizeLessons,
      onClick: () => openCustomizeModal({ initialSection: customizeLessons }),
      icon: customizeLessonsIcon,
      iconSize: 20,
    });
  }

  if (isSpaceDescriptionEnabled && isEventsSpaceType) {
    manageSpaceOptions.push({
      value: customizeSection,
      label: setWelcomeBanner,
      onClick: () =>
        openCustomizeModal({
          initialSection: customizeSection,
          scrollToId: "space-welcome-banner",
        }),
      icon: welcomeBannerIcon,
      iconSize: 20,
    });
  }

  if (isCommunityAdmin(currentCommunityMember)) {
    manageSpaceOptions.push({
      value: customizePaywalls,
      label: customizePaywalls,
      onClick: () => openCustomizeModal({ initialSection: customizePaywalls }),
      icon: creditCardIcon,
      iconSize: 20,
    });
  }

  manageSpaceOptions.push(
    {
      value: customizeSection,
      label: customizeSection,
      onClick: () => openCustomizeModal({ initialSection: customizeSection }),
      icon: customizeIcon,
      iconSize: 20,
    },
    {
      value: customizeLockScreen,
      label: customizeLockScreen,
      onClick: () =>
        openCustomizeModal({ initialSection: customizeLockScreen }),
      icon: customizeLockScreenIcon,
      iconSize: 20,
    },
    {
      value: customizeMembers,
      label: customizeMembers,
      badge: customizeMembersBadge,
      onClick: () => openCustomizeModal({ initialSection: customizeMembers }),
      icon: customizeMembersIcon,
      iconSize: 20,
    },
  );

  if (shouldShowInviteMember && !isLgScreen) {
    const label = !isDraftSpace(space)
      ? t("space_header.invite_member")
      : t("customize_space.add_waitlist");

    manageSpaceOptions.push({
      value: t("space_header.invite_member"),
      label,
      onClick: onInviteMemberClick,
      icon: inviteMemberIcon,
      iconSize: 20,
    });
  }

  manageSpaceOptions.push({
    value: customizeOptions,
    label: customizeOptions,
    onClick: () => openCustomizeModal({ initialSection: customizeOptions }),
    icon: customizeOptionsIcon,
    iconSize: 20,
  });

  if (isUpgradeFlowsForWorkflowsSpacesEnabled) {
    manageSpaceOptions.push({
      value: customizeWorkflows,
      label: customizeWorkflows,
      onClick: () => openCustomizeModal({ initialSection: customizeWorkflows }),
      icon: "lightning",
    });
  }

  return manageSpaceOptions;
};
